import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import NumberWithUnitInputField from 'components/layout/forms/NumberWithUnitInputField';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';
import { useCurrency } from 'services/offers/currency';
import getPositionHours, {
  getInstallationWork,
} from 'services/offers/getPositionHours';
import getInstallationsTotalInstallationPrice from 'services/products/calculations/getInstallationsTotalInstallationPrice';
import {
  getInstallersForWorkDays,
  getWorkDays,
  getWorkMonths,
  getWorkWeeks,
  InstallationTimeField,
  toWorkDays,
} from 'services/offers/workDays';
import { H } from 'services/enums/units';
import translations from 'services/translations/translations';

import 'style/offers/OfferWorkTime.css';

// The key of the row of the whole offer among the work types
const TOTAL = 'total';

// At most two decimals without trailing zeros, with a decimal comma (e.g. 2 or 2,5)
const formatShortNumber = (value: number): string =>
  String(Math.round(value * 100) / 100).replace('.', ',');

// What the user has tried for a row: the installers and the average hourly earnings
interface IRowChoice {
  installers?: string;
  kta?: string;
}

/** The choices of the user for the offer in this browser, by work type */
const readChoices = (storageKey: string): { [row: string]: IRowChoice } => {
  try {
    const stored = window.localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : {};
    // Earlier only the installers were remembered, as texts
    return Object.keys(parsed).reduce(
      (choices: { [row: string]: IRowChoice }, row) => ({
        ...choices,
        [row]:
          typeof parsed[row] === 'string'
            ? { installers: parsed[row] }
            : parsed[row],
      }),
      {}
    );
  } catch (error) {
    return {};
  }
};

/**
 * The installation contract and hours of positions from their primary alternatives: the
 * installation prices of the installation work, and those divided by the average hourly
 * earnings of each position (as the installation contract of the calculation window).
 */
const getContractAndHours = (
  positions: IOfferProductsGet[],
  installationCategories: any[]
): { contract: number; hours: number } =>
  positions.reduce(
    (sums, position) => {
      const primary = position.versions.find((version) => version.isPrimary);
      if (primary === undefined) {
        return sums;
      }

      return {
        contract:
          sums.contract +
          getInstallationsTotalInstallationPrice(
            getInstallationWork(primary.installations, installationCategories),
            position.quantity
          ),
        hours:
          sums.hours +
          getPositionHours(
            primary.installations,
            primary.averageHourlyEarnings,
            position.quantity,
            installationCategories
          ),
      };
    },
    { contract: 0, hours: 0 }
  );

/**
 * The installation time of the offer as in the calculation window of a position, for each
 * work type and the whole offer: the installation contract divided by the average hourly
 * earnings (KTA) gives the hours, shared by the installers into work days, weeks and
 * months (8 h a day, 5 days a week, 21 days a month). Another KTA can be tried, which
 * changes the hours; typing a time gives the installers. The choices are an estimate of
 * the user, remembered in this browser, not saved with the offer.
 */
const OfferWorkTime = ({
  offerId,
  positions,
  type,
}: {
  offerId: number;
  positions: IOfferProductsGet[];
  type: string;
}): JSX.Element | null => {
  const { t } = useTranslation([
    translations.offers,
    translations.products,
    translations.common,
  ]);
  const currency = useCurrency();
  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories ?? []
  );

  const storageKey = `offer-work-time-${type}-${offerId}`;
  const [choices, setChoices] = useState<{ [row: string]: IRowChoice }>(() =>
    readChoices(storageKey)
  );
  const [typedTime, setTypedTime] = useState<{
    field: InstallationTimeField;
    row: string;
    text: string;
  } | null>(null);

  useEffect(() => {
    setChoices(readChoices(storageKey));
    setTypedTime(null);
  }, [storageKey]);

  const saveChoices = (next: { [row: string]: IRowChoice }) => {
    setChoices(next);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch (error) {
      // Only remembered for the user's convenience
    }
  };

  const setChoice = (row: string, choice: IRowChoice) =>
    saveChoices({ ...choices, [row]: { ...choices[row], ...choice } });

  // The KTA of the positions and one installer for every work type
  const reset = () => {
    setTypedTime(null);
    saveChoices({});
  };

  const groups = positions
    .map((position) => position.group)
    .filter((group, index, all) => all.indexOf(group) === index);

  const rows = [
    ...groups.map((group) => ({
      ...getContractAndHours(
        positions.filter((position) => position.group === group),
        installationCategories
      ),
      key: group,
      label: t(`${translations.products}:groups:${group}`),
    })),
    // The whole offer when it has several work types
    ...(groups.length > 1
      ? [
          {
            ...getContractAndHours(positions, installationCategories),
            key: TOTAL,
            label: t(`${translations.offers}:Whole offer total`),
          },
        ]
      : []),
  ].filter((row) => row.contract > 0);

  if (rows.length === 0) {
    return null;
  }

  const shortLabel = (key: string) =>
    t(`${translations.calculationWindow}:${key}`);
  const changed = Object.keys(choices).length > 0 || typedTime !== null;

  return (
    <div className="offer-work-time">
      <div className="offer-work-time-header">
        <h3>{t(`${translations.offers}:workTime.Title`)}</h3>
        <button
          className="offer-work-time-reset"
          disabled={!changed}
          onClick={reset}
          title={t(`${translations.offers}:workTime.Reset help`)}
          type="button"
        >
          ↺ {t(`${translations.offers}:workTime.Reset`)}
        </button>
      </div>
      <p className="offer-work-time-info">
        {t(`${translations.offers}:workTime.Info`)}
      </p>
      <table>
        <thead>
          <tr>
            <th>{t(`${translations.offers}:workTime.Work type`)}</th>
            <th className="numeric">
              {t(`${translations.offers}:workTime.Contract`)}
            </th>
            <th>{t(`${translations.offers}:workTime.KTA`)}</th>
            <th className="numeric">{shortLabel('Total hours')}</th>
            <th>{shortLabel('Installers')}</th>
            <th>{shortLabel('Work days')}</th>
            <th>{shortLabel('Weeks')}</th>
            <th>{shortLabel('Months')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const choice = choices[row.key] ?? {};
            // The KTA of the positions: their contract per their hours
            const positionsKta = row.hours > 0 ? row.contract / row.hours : 0;
            const kta = choice.kta ?? formatShortNumber(positionsKta);
            const ktaValue = numericStringToFloat(kta);
            const hours =
              choice.kta !== undefined && ktaValue > 0
                ? row.contract / ktaValue
                : row.hours;
            const rowInstallers = choice.installers ?? '1';

            const typed =
              typedTime !== null && typedTime.row === row.key
                ? typedTime
                : null;
            const workDays =
              typed === null
                ? getWorkDays(hours, numericStringToFloat(rowInstallers))
                : toWorkDays(typed.field, numericStringToFloat(typed.text));
            const timeValue = (field: InstallationTimeField, value: number) =>
              typed !== null && typed.field === field
                ? typed.text
                : formatGroupedNumber(value);

            // A typed time gives the installers needed for the hours
            const timeHandler =
              (field: InstallationTimeField) =>
              (event: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = event.target;
                setTypedTime({ field, row: row.key, text: value });
                const needed = getInstallersForWorkDays(
                  hours,
                  toWorkDays(field, numericStringToFloat(value))
                );
                if (needed > 0) {
                  setChoice(row.key, { installers: formatShortNumber(needed) });
                }
              };

            return (
              <tr
                className={row.key === TOTAL ? 'offer-work-time-total' : ''}
                key={row.key}
              >
                <td className="offer-work-time-type">{row.label}</td>
                <td className="numeric">
                  {`${formatGroupedNumber(row.contract, 0, 0)} ${currency}`}
                </td>
                <td
                  className={
                    choice.kta !== undefined ? 'offer-work-time-changed' : ''
                  }
                  title={
                    choice.kta !== undefined
                      ? `${t(
                          `${translations.offers}:workTime.Changed`
                        )} (${formatShortNumber(positionsKta)} ${currency}/h)`
                      : undefined
                  }
                >
                  <NumberWithUnitInputField
                    name="kta"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setTypedTime(null);
                      setChoice(row.key, { kta: event.target.value });
                    }}
                    unit={`${currency}/h`}
                    value={kta}
                  />
                </td>
                <td className="numeric offer-work-time-hours">
                  {`${formatGroupedNumber(hours)} ${H}`}
                </td>
                <td>
                  <NumberWithUnitInputField
                    name="installers"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setTypedTime(null);
                      setChoice(row.key, { installers: event.target.value });
                    }}
                    unit={shortLabel('installers-short')}
                    value={rowInstallers}
                  />
                </td>
                <td>
                  <NumberWithUnitInputField
                    name="workDays"
                    onChange={timeHandler('days')}
                    unit={shortLabel('work-days-short')}
                    value={timeValue('days', workDays)}
                  />
                </td>
                <td>
                  <NumberWithUnitInputField
                    name="workWeeks"
                    onChange={timeHandler('weeks')}
                    unit={shortLabel('weeks-short')}
                    value={timeValue('weeks', getWorkWeeks(workDays))}
                  />
                </td>
                <td>
                  <NumberWithUnitInputField
                    name="workMonths"
                    onChange={timeHandler('months')}
                    unit={shortLabel('months-short')}
                    value={timeValue('months', getWorkMonths(workDays))}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default OfferWorkTime;
